import { useState } from 'react';
import { Form, Input, Select, Button, Spin } from 'antd';
import { toast } from 'react-toastify';
import { useHistory, useLocation } from 'react-router-dom';
import { axiosInstanceB2B, Toaster } from '@share/utils';

import img from '@assets/images/element/signin.svg';

import './style.scss';
import { FormattedMessage, useIntl } from 'react-intl';

interface IRegisterCompany {
  annualTravelSpend: string;
  businessEmail: string;
  businessPhone: string;
  companyName: string;
  companyRole: string;
  companyWebsite: string;
  firstName: string;
  lastName: string;
}

const Register = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  const [form] = Form.useForm();

  const search = useLocation().search;
  const email = new URLSearchParams(search).get('email');

  const onFinish = async (values: IRegisterCompany) => {
    setLoading(true);

    const payload = {
      ...values,
      shortCompanyName: values.companyName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase(),
    }
    try {
      const { data: result } = await axiosInstanceB2B.post('/identity/account/register', payload);

      if (result?.success) {
        toast.success(`${intl.formatMessage({ id: 'company.registered' })}`, {
          theme: 'colored',
        });
        history.push('/dashboard');
      }
    } catch (error: any) {
      Toaster.error(error.response?.data?.errors[0]?.message);
    } finally {
      setLoading(false);
    }
  };

  const intl = useIntl();

  return (
    <section className="registerSec">
      <div className="container h-100 d-flex px-3 px-md-4 py-3 py-md-0">
        <div className="row justify-content-center align-items-center m-auto" style={{ width: '100%' }}>
          <div className="col-12">
            <div className="bg-mode shadow rounded-3">
              <Spin spinning={loading}>
                <div className="row g-0">
                  <div className="col-lg-6 d-flex align-items-top">
                    <div className="p-3 p-lg-5">
                      <img src={img} alt="" />
                    </div>
                    <div className="vr opacity-1 d-none d-lg-block"></div>
                  </div>
                  <div className="col-lg-6 order-1">
                    <div className="p-sm-4 p-3 p-sm-6">
                      <h1 className="mb-2 h3"><FormattedMessage id="register.company" /></h1>
                      <p className="mb-3">
                        <FormattedMessage id="register.free.message" />
                      </p>

                      <Form
                        form={form}
                        onFinish={onFinish}
                        className="mt-4 text-start"
                        layout="vertical"
                        initialValues={{ businessEmail: email }}
                      >
                        <Form.Item
                          label={intl.formatMessage({ id: 'last.name' })}
                          name="firstName"
                          rules={[{ required: true, message: intl.formatMessage({ id: 'first.name.message' }) }]}
                        >
                          <Input
                            className="form-control form-control-lg"
                            placeholder={intl.formatMessage({ id: 'enter.first.name' })}
                          />
                        </Form.Item>

                        <Form.Item
                          label="First name"
                          name="lastName"
                          rules={[{ required: true, message: 'Please input your last name!' }]}
                        >
                          <Input
                            className="form-control form-control-lg"
                            placeholder="Enter Last Name"
                          />
                        </Form.Item>

                        <Form.Item
                          label="Company name"
                          name="companyName"
                          rules={[{ required: true, message: 'Please input your company name!' }]}
                        >
                          <Input
                            className="form-control form-control-lg"
                            placeholder="Enter Company Name"
                          />
                        </Form.Item>

                        {/* <Form.Item
                          label="Short Company name"
                          name="shortCompanyName"
                          rules={[
                            { required: true, message: 'Please input your account company name!' },
                          ]}
                        >
                          <Input
                            className="form-control form-control-lg"
                            placeholder="Account Company Name"
                          />
                        </Form.Item> */}

                        <Form.Item
                          label="Business email"
                          name="businessEmail"
                          rules={[
                            {
                              required: true,
                              message: 'Please input your business email!',
                              pattern: new RegExp('^\\S+@\\S+\\.\\S+$'),
                            },
                          ]}
                        >
                          <Input
                            className="form-control form-control-lg"
                            placeholder="Enter Business Email"
                          />
                        </Form.Item>

                        <Form.Item
                          label="Company website"
                          name="companyWebsite"
                          rules={[
                            { required: true, message: 'Please input your company website!' },
                          ]}
                        >
                          <Input
                            className="form-control form-control-lg"
                            placeholder="Enter Company Website"
                          />
                        </Form.Item>

                        <Form.Item
                          label="Business phone"
                          name="businessPhone"
                          rules={[{ required: true, message: 'Please input your business phone!' }]}
                        >
                          <Input
                            className="form-control form-control-lg"
                            placeholder="Enter Business Phone"
                          />
                        </Form.Item>

                        <Form.Item
                          label="Role in the company"
                          name="companyRole"
                          rules={[
                            {
                              required: true,
                              message: 'Please select your estimated annual travel spend!',
                            },
                          ]}
                        >
                          <Select placeholder="Enter Company Role" className="customSelect">
                            <Select.Option value="TRAVBUSINESS_ADMIN">Admin</Select.Option>
                            <Select.Option value="TRAVBUSINESS_TRAVELER">Employee/Traveler</Select.Option>
                            <Select.Option value="TRAVBUSINESS_COORDINATOR">
                              Coordinator
                            </Select.Option>
                          </Select>
                        </Form.Item>

                        <Form.Item
                          label="Estimated annual travel spend"
                          name="annualTravelSpend"
                          rules={[
                            { required: true, message: 'Please input your role in the company!' },
                          ]}
                        >
                          <Select placeholder="Enter Annual Travel Spend" className="customSelect">
                            <Select.Option value="0-25k">$0 - $25,000</Select.Option>
                            <Select.Option value="25k-50k">$25,000 - $50,000</Select.Option>
                            <Select.Option value="50k-100k">$50,000 - $100,000</Select.Option>
                            <Select.Option value="100k-250k">$100,000 - $250,000</Select.Option>
                            <Select.Option value="250k+">$250,000+</Select.Option>
                          </Select>
                        </Form.Item>

                        <Form.Item className='mb-3'>
                          <Button
                            type="primary"
                            htmlType="submit"
                            block
                            className="mt-3"
                            size={'large'}
                          >
                            {loading ? (
                              <div>
                                <Spin size="small" />
                              </div>
                            ) : (
                              'Register my Free Account'
                            )}
                          </Button>
                        </Form.Item>
                      </Form>
                    </div>
                  </div>
                </div>
              </Spin>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Register;
